import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
/* eslint-disable import/export */
export abstract class UpdateAllowedDomainRPC implements
  LeoRPC<UpdateAllowedDomainRPC.Request, UpdateAllowedDomainRPC.Response, UpdateAllowedDomainRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateAllowedDomainRPC.Request
  ): Promise<LeoRPCResult<UpdateAllowedDomainRPC.Response, UpdateAllowedDomainRPC.Errors.Errors>>;
}

export namespace UpdateAllowedDomainRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    domainId: string;
    domainName: BoundedNameDTO;
    domain: BoundedNameDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateAllowedDomainRPCRequest: boolean = true;
  
    readonly domainId: LeoUUID;
    readonly domainName: BoundedName;
    readonly domain: BoundedName;
  
  
    constructor(
    domainId: LeoUUID, 
    domainName: BoundedName, 
    domain: BoundedName
    ) {
      this.domainId = domainId;
      this.domainName = domainName;
      this.domain = domain;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const domainId = getLeoUuid(dto, "domainId", InvalidRequestDomainIdError);
      const domainName = BoundedName.fromDTO(getObject(dto, "domainName", InvalidRequestDomainNameError));
      const domain = BoundedName.fromDTO(getObject(dto, "domain", InvalidRequestDomainError));
      return new Request(
        domainId,
        domainName,
        domain
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          domainId: this.domainId.toDTO(),
          domainName: this.domainName.toDTO(),
          domain: this.domain.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateAllowedDomainRPC(domainId='${this.domainId}',domainName='${this.domainName}',domain='${this.domain}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateAllowedDomainRPC.Response()`;
  };

  export enum RPCError {
    DomainNotFound = "DOMAIN_NOT_FOUND",
    DomainAlreadyExists = "DOMAIN_ALREADY_EXISTS",
    DomainNameAlreadyExists = "DOMAIN_NAME_ALREADY_EXISTS"
  }

  export namespace Errors {
    
    export interface DomainNotFoundDTO {
      code: string;
    }
    
    export class DomainNotFound extends LeoRPCError<RPCError> {
      readonly isDomainNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DomainNotFound;
        super(code);
      }
    
    
      toDTO(): DomainNotFoundDTO {
        const dto: DomainNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DomainNotFound.prototype.toString = function toString(): string {
      return `UpdateAllowedDomainRPC.DOMAIN_NOT_FOUND()`;
    };
    
    
    export interface DomainAlreadyExistsDTO {
      code: string;
    }
    
    export class DomainAlreadyExists extends LeoRPCError<RPCError> {
      readonly isDomainAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DomainAlreadyExists;
        super(code);
      }
    
    
      toDTO(): DomainAlreadyExistsDTO {
        const dto: DomainAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DomainAlreadyExists.prototype.toString = function toString(): string {
      return `UpdateAllowedDomainRPC.DOMAIN_ALREADY_EXISTS()`;
    };
    
    
    export interface DomainNameAlreadyExistsDTO {
      code: string;
    }
    
    export class DomainNameAlreadyExists extends LeoRPCError<RPCError> {
      readonly isDomainNameAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DomainNameAlreadyExists;
        super(code);
      }
    
    
      toDTO(): DomainNameAlreadyExistsDTO {
        const dto: DomainNameAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DomainNameAlreadyExists.prototype.toString = function toString(): string {
      return `UpdateAllowedDomainRPC.DOMAIN_NAME_ALREADY_EXISTS()`;
    };
    

    export type Errors = DomainNotFound | DomainAlreadyExists | DomainNameAlreadyExists;
    export type ErrorsDTO = DomainNotFoundDTO | DomainAlreadyExistsDTO | DomainNameAlreadyExistsDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.DomainNotFound:
          return new DomainNotFound();
        case RPCError.DomainAlreadyExists:
          return new DomainAlreadyExists();
        case RPCError.DomainNameAlreadyExists:
          return new DomainNameAlreadyExists();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestDomainIdError extends UpdateAllowedDomainRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDomainIdError";
  }
}

export class InvalidRequestDomainNameError extends UpdateAllowedDomainRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDomainNameError";
  }
}

export class InvalidRequestDomainError extends UpdateAllowedDomainRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDomainError";
  }
}


export class InvalidResponseDomainNotFoundError extends UpdateAllowedDomainRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainNotFoundError";
  }
}

export class InvalidResponseDomainAlreadyExistsError extends UpdateAllowedDomainRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainAlreadyExistsError";
  }
}

export class InvalidResponseDomainNameAlreadyExistsError extends UpdateAllowedDomainRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainNameAlreadyExistsError";
  }
}

